import {Component, EventEmitter, forwardRef, Inject, Input, OnChanges, OnDestroy, OnInit, Output} from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { ControlValueAccessor, FormBuilder, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DOCUMENT } from '@angular/common';
import { debounceTime, distinctUntilChanged, startWith, takeUntil } from 'rxjs/operators';
import { ListResponse } from '../../interface/response/list-response';
import { MatSelectChange } from '@angular/material/select';
import { Device } from "../../interface/device";
import { LicenseService } from "../../services/license.service";
import {User} from "../../interface/user";

@Component({
  selector: 'app-dict-user-devices',
  templateUrl: './dict-user-devices.component.html',
  styleUrls: ['./dict-user-devices.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => DictUserDevicesComponent) }
  ]
})
export class DictUserDevicesComponent implements OnInit, OnDestroy, ControlValueAccessor, OnChanges {

  @Input() label: string;
  @Input() multiple = false;
  @Input() control: FormControl = null;
  @Input() required = false;
  @Input() filterUser?: User;
  @Input() deviceItems: Device[] = null;
  @Output() selectedDevice: EventEmitter<Device> = new EventEmitter<Device>();
  @Input() minimalDisplay = false;
  @Input() disabled = false;

  externalControl = true;
  items: Device[] = [];
  value?: string = null;
  userDevicesFilterCtrl: FormControl;
  itemsSubject: ReplaySubject<ListResponse<Device>> = new ReplaySubject<ListResponse<Device>>(1);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private licenseService: LicenseService,
    @Inject(DOCUMENT) private document: Document,
    private fb: FormBuilder,
  ) {
    this.userDevicesFilterCtrl = fb.control('');
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  ngOnInit(): void {
    if (this.deviceItems !== null) {
      this.items = this.deviceItems;
    }
    if (this.control === null) {
      this.control = new FormControl('');
      this.externalControl = false;
    }

    const initialFilter: any = {};
    if (this.filterUser) {
      initialFilter.user = this.filterUser.uuid;
    }
    this.licenseService.loadDeviceNotCountedList(0, 9, initialFilter, this.itemsSubject);

    this.control.valueChanges
      .pipe(
        startWith(this.control.value),
        takeUntil(this.destroyed$)
      )
      .subscribe((val: string) => {
        if (val) {
          let exists = false;
          this.items.forEach((device: Device) => {
            if (device.uuid === val) {
              exists = true;
            }
          });

          if (!exists) {
            const filter: any = {
              in$uuid: val,
            };
            if (this.filterUser) {
              filter.user = this.filterUser.uuid;
            }
            this.licenseService.loadDeviceNotCountedList(0, 9, filter, this.itemsSubject);
          }
        }
      });

    this.userDevicesFilterCtrl.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((val) => {
        const baseFilter: any = {};

        if (this.filterUser) {
          baseFilter.user = this.filterUser.uuid;
        }

        if (val) {
          this.licenseService.loadDeviceNotCountedList(0, 5, {
            ...baseFilter,
            context: val,
          }, this.itemsSubject);
        } else {
          if (this.control.value) {
            this.licenseService.loadDeviceNotCountedList(0, 9, {
              ...baseFilter,
              in$uuid: this.control.value,
            }, this.itemsSubject);
          } else {
            this.licenseService.loadDeviceNotCountedList(0, 9, baseFilter, this.itemsSubject);
          }
        }
      });

    this.itemsSubject
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: ListResponse<Device>) => {
        if ( this.deviceItems === null) {
          this.items = result.items;
        } else{
          this.items = this.deviceItems;
        }
      });
  }

  ngOnChanges(): void {
    if (this.filterUser) {
      this.licenseService.loadDeviceNotCountedList(0, 10, {
        user: this.filterUser.uuid
      }, this.itemsSubject);
      return;
    }

    if (!this.filterUser) {
      this.items = [];
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    const shouldBeDisabled = isDisabled || this.disabled;

    if (shouldBeDisabled) {
      this.control.disable();
      this.userDevicesFilterCtrl.disable();
    } else {
      this.control.enable();
      this.userDevicesFilterCtrl.enable();
    }
  }

  writeValue(value: any): void {
    if (this.externalControl) {
      return;
    }

    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(e: MatSelectChange): void {
    this.items.forEach((device: Device) => {
      if (device.uuid === e.value) {
        this.selectedDevice.emit(device);
      }
    });

    this.value = e.value;
    this.onChange(this.value);
  }

  clearValue(): void {
    this.control.setValue(this.multiple ? [] : '');
    this.writeValue(this.multiple ? [] : '');
    this.onChange(this.value);
  }
}
